import { formatCurrency } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, Signal } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

import { SupportedLanguageCodes } from '@ph-static-data/languages';
import { download, generateCsv, mkConfig } from 'export-to-csv';

import { ConfigService } from '@ph-core/services/config/config.service';
import { PH_OVERLAY_DATA } from '@ph-core/services/overlay';
import { LazyTemplateService } from '@ph-core/services/template/lazy-template.service';
import { dealerIdPresentationPreference, getDisplayDealerId } from '@ph-core/utils/dealer-id-presentation';
import { buildRemitRegisterCsv } from '@ph-core/utils/remit-register-columns';
import { sanitizeDataForCsv } from '@ph-core/utils/sanitize-data-for-csv';
import { ContractType } from '@ph-model/api';
import { PendingContractResponse } from '@ph-model/api/response/pending-contract.response.model';
import { ExportPdfAcceptedData } from '@ph-model/export-pdf-accepted-data';
import { RemitStore, RemitStoreType } from '@ph-remit/store/remit/remit.store';
import { PhOverlayCloseDirective } from '@ph-shared/directives/overlay/overlay-close.directive';
import { calcContractTotal } from '@ph-shared/utils/calc-contract-total';
import { PrismicStore, PrismicStoreType } from '@ph-store/prismic/prismic.store';
import { UserStore } from '@ph-store/user/user.store';

import { IconComponent } from '../icon/icon-component';
import { PhButtonComponent } from '../ph-button/ph-button.component';

@Component({
  templateUrl: './remit-submitted-popup.component.html',
  styleUrls: ['./remit-submitted-popup.component.scss'],
  imports: [TranslateModule, IconComponent, PhButtonComponent, PhOverlayCloseDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemitSubmittedPopupComponent {
  errorInContracts: boolean = false;
  displayErrors: { name: string; error: string }[] = [];
  atLeastOneSuccessfulContract: boolean = true;

  configService = inject(ConfigService);
  environment = this.configService.environment;
  supportContractRemittance: boolean = this.environment.features.remit.supportContractRemittance;
  prismicStore: PrismicStoreType = inject(PrismicStore);

  readonly userStore = inject(UserStore);
  readonly hideDealerCost: boolean =
    !this.userStore.user()?.scopes || this.userStore.user()?.scopes.includes('HIDE_DEALER_COST') || false;

  private data = inject<{ contracts: PendingContractResponse }>(PH_OVERLAY_DATA);

  private remitStore: RemitStoreType = inject(RemitStore);
  private _submittedPendingContracts: ContractType[] = [];
  private _navbarLogo: Signal<string> = computed(() => {
    const isFrenchSelected: boolean = localStorage.getItem('languageCode') === 'fr-CA';
    const data = this.prismicStore.data();

    return (isFrenchSelected ? data?.small_logo_fr.url || data?.small_logo.url : data?.small_logo.url) ?? '';
  });
  private _pendingContractResponse: PendingContractResponse;
  private templateService = inject(LazyTemplateService);

  constructor() {
    this._pendingContractResponse = this.data.contracts;

    effect(() => {
      this._submittedPendingContracts = this.remitStore.submittedPendingContracts();
      this._checkForErroredContracts(this._pendingContractResponse, this.remitStore.submittedPendingContracts());
    });
  }

  printRemit(): void {
    const successfulContracts = this._prepareSuccessfulContracts();
    const total = this._getTotalDealerCost(successfulContracts);
    this.templateService.printRemit(successfulContracts, true, this._navbarLogo(), total);
  }

  exportClicked(): void {
    const userData = this.userStore.user();
    const successfulContracts = this._prepareSuccessfulContracts();

    const dealerNumber = getDisplayDealerId(userData, dealerIdPresentationPreference(this.configService));
    const { headers, rows } = buildRemitRegisterCsv(
      successfulContracts,
      this.environment.features.remit.registerColumns.csv,
      {
        hideDealerCost: this.hideDealerCost,
        dealerNumber,
        totalDealerCost: formatCurrency(
          this._getTotalDealerCost(successfulContracts),
          SupportedLanguageCodes.English,
          '$'
        ),
      }
    );
    this._exportToCSV(rows, this._getFileName(successfulContracts), headers);
  }

  private _getTotalDealerCost(contracts: ContractType[]): number {
    return calcContractTotal(contracts);
  }

  private _prepareSuccessfulContracts(): ContractType[] {
    const remittedErrorContracts =
      this._pendingContractResponse?.erroredContracts?.remittedErrorContracts?.map(
        (c: ContractType) => c.econContractId
      ) || [];
    const declinedErrorContracts =
      this._pendingContractResponse?.erroredContracts?.declinedErrorContracts?.map(
        (c: ContractType) => c.econContractId
      ) || [];

    const unsuccessfulContractIDs = [...remittedErrorContracts, ...declinedErrorContracts];

    return this._submittedPendingContracts.filter(
      (c: ContractType) => !unsuccessfulContractIDs.includes(c.econContractId)
    );
  }

  private _checkForErroredContracts(
    response: PendingContractResponse,
    submittedContracts: ContractType[] = []
  ): ContractType[] {
    this.errorInContracts = false;
    this.atLeastOneSuccessfulContract = false;
    this.displayErrors = [];

    const contracts = response?.erroredContracts;
    if (contracts) {
      if (contracts.declinedErrorContracts && contracts.declinedErrorContracts.length > 0) {
        this.errorInContracts = true;

        this.displayErrors = contracts.declinedErrorContracts.map((data: ContractType) => {
          return {
            name: `${data.firstName} ${data.lastName} - ${data.description}`,
            error: data.standardResponse.responseDescription,
          };
        });
      }

      if (contracts.remittedErrorContracts && contracts.remittedErrorContracts.length > 0) {
        this.errorInContracts = true;

        this.displayErrors = contracts.remittedErrorContracts.map((data: ContractType) => {
          return {
            name: `${data.firstName} ${data.lastName} - ${data.description}`,
            error: data.standardResponse.responseDescription,
          };
        });
      }
      if (this.displayErrors.length < submittedContracts.length) {
        this.atLeastOneSuccessfulContract = true;
      }

      return submittedContracts.filter((contract: ContractType) => {
        const foundIndx = contracts.remittedErrorContracts.findIndex(
          (x: ContractType) => x.econContractId === contract.econContractId
        );

        return foundIndx <= -1;
      });
    } else if (this._submittedPendingContracts.length) {
      this.atLeastOneSuccessfulContract = true;
    }

    return submittedContracts;
  }

  private _exportToCSV(
    data: Record<string, ExportPdfAcceptedData>[],
    filename: string,
    columnHeaders: { key: string; displayLabel: string }[]
  ): void {
    const csvConfig = mkConfig({ filename, columnHeaders });

    const sanitizedData: Record<string, ExportPdfAcceptedData>[] = sanitizeDataForCsv(
      data as unknown as Record<string, ExportPdfAcceptedData>[]
    );

    const csv = generateCsv(csvConfig)(sanitizedData);

    download(csvConfig)(csv);
  }

  private _getFileName(contracts: ContractType[]): string {
    const prependName = 'Remittance Register';
    if (!contracts.length || !contracts[0]) {
      return prependName;
    }
    const firstContract = contracts[0];
    const appendDateName =
      firstContract && contracts.every((c: ContractType) => c.contractGroup2 === firstContract.contractGroup2)
        ? firstContract.contractGroup2
        : firstContract.contractGroup1;

    return prependName + ' ' + appendDateName;
  }
}
